import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { link, paragraphRegular } from '@helpers/typography';
import styled from 'styled-components';
const Container = styled.nav `
  display: flex;
  gap: ${fluid(24, 46, Device.Desktop)};
  padding: 0 ${fluid(16, 24)};
  margin: 0 0 0 auto;

  [data-sticky='true'] & {
    gap: ${fluid(24, 28)};

    @media ${from(Device.TabletXL)} {
      padding-right: 8px;
    }

    @media ${from(Device.Desktop)} {
      padding-right: 0;
    }
  }

  @media ${until(Device.Desktop)} {
    gap: ${fluid(14, 18, Device.TabletLarge, Device.Desktop)} !important;
  }
`;
const TopLink = styled.a `
  ${ButtonReset};
  ${transition('color')};
  ${paragraphRegular};

  display: inline-flex;
  position: relative;
  height: 100%;
  margin: 0;
  white-space: nowrap;
  color: ${brand.primary.offWhite};
  text-decoration: none;

  [data-sticky='true'] & {
    padding-top: 8px;
  }

  button& {
    position: relative;
    z-index: 1;

    &::after {
      ${transition('background-size')};

      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      left: 0;
      height: 6px;
      width: 100%;
      background: transparent;
      background-image: linear-gradient(to right, ${brand.secondary.red}, ${brand.secondary.red});
      background-repeat: no-repeat;
      background-size: 0% 100%;
      transform: translateY(100%);
    }
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &[data-active='true'],
  &:hover {
    color: ${brand.secondary.red};

    &::after {
      background-size: 100% 100%;
    }
  }

  [data-sticky='false'] & {
    @media ${until(Device.DesktopSmall)} {
      font-size: ${fluid(16, 18, Device.TabletLarge, Device.DesktopSmall)};
    }
  }

  [data-sticky='true'] & {
    @media ${until(Device.DesktopSmall)} {
      font-size: ${fluid(14, 16, Device.TabletLarge, Device.DesktopSmall)};
    }
  }
`;
const NavigationDropdown = styled.div `
  --maxHeight: calc(100vh - var(--headerHeight) - ${fluid(16, 24)});

  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: var(--gutterWidth);
  position: fixed;
  top: var(--headerHeight);
  left: 50%;
  transform: translateX(-50%);
  max-width: var(--siteWidth);
  width: calc(100% - (var(--sitePadding) * 2));

  /* Ensure background click isn't interfered with */
  pointer-events: none;

  [data-sticky='true'] & {
    --maxHeight: calc(100vh - var(--stickyHeaderHeight));

    top: var(--stickyHeaderHeight);
  }
`;
const SecondLevelContainer = styled.div `
  ${transition('max-height', 1.2)}

  grid-column: 1 / span 12;
  grid-row: 1;
  max-height: var(--maxHeight);
  overflow: hidden;

  &[data-active='false'] {
    max-height: 0;
    tabindex: -1;
  }

  &[data-third-only='true'] {
    height: 300px;
    background-color: ${brand.primary.offWhite};
  }
`;
const SecondLevelList = styled.ul `
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: min-content;
  gap: ${fluid(8, 10)} var(--gutterWidth);
  background-color: ${brand.primary.offWhite};
  padding: ${fluid(30, 40)};
  pointer-events: all;

  > li {
    grid-column: 2 / span 3;
  }
`;
const ThirdLevelContainer = styled.div `
  ${transition('max-height', 1.2)}

  grid-column: 5 / span 7;
  grid-row: 1 / span 2;

  display: flex;
  flex-direction: column;
  max-height: var(--maxHeight);
  overflow: hidden;
  pointer-events: all;
  position: relative;

  &[data-active='false'] {
    max-height: 0;
    tabindex: -1;
  }

  &[data-third-only='true'] {
    grid-column: 3 / span 8;
  }
`;
const ThirdLevelList = styled.div `
  ${scrollbars};

  display: grid;
  grid-template-columns: repeat(7, 1fr);
  column-gap: ${fluid(26, 28)};
  background: ${brand.white};
  padding: ${fluid(26, 30)} ${fluid(24, 28)};
  overflow: hidden;

  ${ThirdLevelContainer}[data-open='true'] & {
    overflow: auto;
  }
`;
const ThirdLevelFirstLink = styled.div `
  grid-column: 1 / span 7;
  display: flex;
`;
const LevelTwoLink = styled.a `
  ${ButtonReset};
  ${transition('background-size')};
  ${paragraphRegular};
  ${fonts.graphikCompact.semibold};

  color: ${brand.primary.purple};
  text-decoration: none;
  text-align: left;
  background: transparent;
  background-image: linear-gradient(to right, ${brand.white}, ${brand.white});
  background-repeat: no-repeat;
  background-size: 0% 100%;
  display: flex;
  gap: ${fluid(24, 28)};
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 7px 12px 13px;
  margin: 0;

  > svg {
    ${transition('transform')};

    display: block;
    min-width: 20px;
    max-width: 20px;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &[data-active='true'],
  &:hover {
    background-size: 100% 100%;

    > svg {
      transform: translateX(15%);
    }
  }

  ${ThirdLevelFirstLink} &,
  li:first-child & {
    ${fonts.graphikCompact.medium};

    color: ${brand.secondary.red};
    font-size: ${fluid(22, 24)};
    line-height: ${fluid(28, 30)};
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: ${fluid(16, 20)};
    background-image: none;
  }

  ${ThirdLevelFirstLink} & {
    padding: 0;
    width: auto;
    margin-bottom: ${fluid(24, 28)};
  }
`;
const LevelThreeLink = styled.a `
  ${link};
  ${paragraphRegular};
  ${transition('text-decoration-color')};

  color: ${brand.primary.purple};
  margin: 0;
  text-decoration-color: transparent;
  text-underline-offset: 2px;

  li:first-child & {
    ${fonts.graphikCompact.semibold};
  }

  a& {
    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-style: solid;
      outline-width: 2px;
    }

    &.focus-ring,
    &:hover {
      text-decoration-color: currentColor;
    }
  }
`;
const LinkColumn = styled.div `
  grid-column: span 3;
`;
const LinkGroup = styled.ul `
  grid-column: span 3;
  margin: 0 0 16px;

  > li {
    margin: 0 0 12px;
  }
`;
const CloseButton = styled.button `
  ${ButtonReset};
  ${transition('opacity, transform')};

  height: ${fluid(16, 18)};
  width: ${fluid(16, 18)};
  position: absolute;
  top: ${fluid(26, 30)};
  right: ${fluid(26, 30)};
  border-radius: 5000px;
  color: ${brand.tertiary.darkBlue};

  > svg {
    display: block;
    width: 100%;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-style: solid;
    outline-width: 2px;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.66;
    transform: scale(1.15);
  }

  [data-active='false'] & {
    display: none;
  }
`;
const FeaturedCard = styled.div `
  padding: ${fluid(38, 40)} ${fluid(24, 28)} ${fluid(26, 30)};
  background-color: ${brand.white};
  position: relative;

  &::before {
    content: '';
    display: block;
    height: 1px;
    width: calc(100% - ${fluid(48, 56)});
    background-color: ${brand.primary.purple};
    position: absolute;
    left: 50%;
    top: ${fluid(10, 12)};
    transform: translateX(-50%);
  }
`;
const DesktopNavigationStyles = {
    CloseButton,
    Container,
    FeaturedCard,
    LevelThreeLink,
    LevelTwoLink,
    LinkColumn,
    LinkGroup,
    NavigationDropdown,
    SecondLevelList,
    SecondLevelContainer,
    ThirdLevelFirstLink,
    ThirdLevelList,
    ThirdLevelContainer,
    TopLink,
};
export default DesktopNavigationStyles;
